import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Pencil, Trash2, Download, Lock, Unlock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";

const MENU_WIDTH = 208; // w-52

const ClassRowActionMenu = ({
  classroom,
  isOpen,
  onOpenChange,
  onEdit,
  onDelete,
  onDownload,
  onToggleStatus,
}) => {
  const buttonWrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const canUpdate = hasPermission("elearning.classes", "U");
  const canDelete = hasPermission("elearning.classes", "D");
  const canDownload = hasPermission("elearning.classes", "R");
  const isActive =
    classroom?.is_active === true || classroom?.status === "active";

  const hasAnyAction = canUpdate || canDelete || canDownload;

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const left = Math.min(
      Math.max(8, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8
    );
    setMenuPos({
      top: rect.bottom + 4,
      left,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!hasAnyAction || !isOpen) return undefined;
    const handleOutside = (e) => {
      const inButton = buttonWrapRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inButton && !inMenu) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [hasAnyAction, isOpen, onOpenChange]);

  if (!hasAnyAction) return null;

  const menuPanel = (
    <AnimatePresence>
      {isOpen && menuPos ? (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, scale: 0.95, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 4 }}
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            width: MENU_WIDTH,
            zIndex: 9999,
          }}
          className="rounded-2xl border border-gray-100 bg-white p-1.5 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {canUpdate ? (
            <button
              type="button"
              onClick={() => {
                onEdit?.(classroom);
                onOpenChange(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <Pencil size={15} className="text-amber-600" />
              Sửa
            </button>
          ) : null}

          {canDownload ? (
            <button
              type="button"
              onClick={() => {
                onDownload?.(classroom);
                onOpenChange(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-gray-600 hover:bg-indigo-50 hover:text-indigo-700"
            >
              <Download size={15} className="text-indigo-600" />
              Tải DS học viên
            </button>
          ) : null}

          {canUpdate ? (
            <button
              type="button"
              onClick={() => {
                onToggleStatus?.(classroom);
                onOpenChange(false);
              }}
              className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold ${
                isActive
                  ? "text-orange-700 hover:bg-orange-50"
                  : "text-green-700 hover:bg-green-50"
              }`}
            >
              {isActive ? (
                <Lock size={15} className="text-orange-600" />
              ) : (
                <Unlock size={15} className="text-green-600" />
              )}
              {isActive ? "Đóng lớp" : "Mở lại lớp"}
            </button>
          ) : null}

          {canDelete ? (
            <button
              type="button"
              onClick={() => {
                onDelete?.(classroom);
                onOpenChange(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-red-600 hover:bg-red-50"
            >
              <Trash2 size={15} />
              Xóa
            </button>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <>
      <div
        className="relative inline-flex"
        ref={buttonWrapRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={buttonRef}
          type="button"
          title="Tác vụ"
          aria-label="Mở menu tác vụ"
          aria-expanded={isOpen}
          onClick={() => onOpenChange(!isOpen)}
          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
            isOpen
              ? "border-gray-300 bg-gray-100 text-gray-700"
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          <MoreVertical size={18} />
        </button>
      </div>
      {typeof document !== "undefined"
        ? createPortal(menuPanel, document.body)
        : null}
    </>
  );
};

export default ClassRowActionMenu;
